const MONEY_PATTERN = /^([+-]?)(\d+)(?:\.(\d{0,2}))?$/;

interface ParsedMoney {
  minor: bigint;
  negative: boolean;
}

export type MoneyInput = string | number;

function parseMoney(value: MoneyInput): ParsedMoney | null {
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  const trimmed = String(value).trim();
  const match = MONEY_PATTERN.exec(trimmed);
  if (!match) return null;

  const sign = match[1] ?? '';
  const whole = match[2];
  const fraction = match[3] ?? '';
  if (!whole) return null;
  const negative = sign === '-';
  const absoluteMinor = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  return {
    minor: negative ? -absoluteMinor : absoluteMinor,
    negative: negative && absoluteMinor !== 0n,
  };
}

export function moneyToMinor(value: MoneyInput): bigint | null {
  return parseMoney(value)?.minor ?? null;
}

export function minorToMoney(minor: bigint): string {
  const negative = minor < 0n;
  const absolute = negative ? -minor : minor;
  const whole = absolute / 100n;
  const fraction = String(absolute % 100n).padStart(2, '0');
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/** Normalize user-entered MYR to an exact two-decimal string. */
export function normalizeMoneyInput(value: string): string | null {
  const parsed = parseMoney(value);
  if (!parsed || parsed.minor < 0n) return null;
  return minorToMoney(parsed.minor);
}

export function isPositiveMoney(value: MoneyInput): boolean {
  const minor = moneyToMinor(value);
  return minor != null && minor > 0n;
}

export function isNonZeroMoney(value: MoneyInput): boolean {
  const minor = moneyToMinor(value);
  return minor != null && minor !== 0n;
}

export function compareMoney(left: MoneyInput, right: MoneyInput): -1 | 0 | 1 {
  const leftMinor = moneyToMinor(left);
  const rightMinor = moneyToMinor(right);
  if (leftMinor == null || rightMinor == null) {
    throw new Error('Invalid money value');
  }
  if (leftMinor < rightMinor) return -1;
  if (leftMinor > rightMinor) return 1;
  return 0;
}

export function multiplyMoneyByInteger(value: MoneyInput, multiplier: number): string {
  if (!Number.isSafeInteger(multiplier)) {
    throw new Error('Money multiplier must be a safe integer');
  }
  const minor = moneyToMinor(value);
  if (minor == null) throw new Error('Invalid money value');
  return minorToMoney(minor * BigInt(multiplier));
}

export function absoluteMoney(value: MoneyInput): string {
  const minor = moneyToMinor(value);
  if (minor == null) throw new Error('Invalid money value');
  return minorToMoney(minor < 0n ? -minor : minor);
}
